import { createEffect, createSignal } from "solid-js";

import { Show } from "solid-js/web";
import { usePromptContext } from "../PromptContext";
import { For, Match, onCleanup, onMount, Switch } from "solid-js";
import type { Editor } from "@tiptap/core";
import { debounce } from "@solid-primitives/scheduled";
import { useWire } from "@core/wire/provider";
import { type curator, getRequestClient, type operation } from "@repo/client";
import { createStore } from "solid-js/store";
import { parsePromptTextMentions } from "@core/lib/parsePromptTextMentions";
import { ImprovePromptModal, PromptAssetCTA, PromptCollectionCTA } from "@core/screens/modals/ImprovePromptModal";
import { getPromptTextAndDecorations } from "../getPromptTextAndDecorations";
import { useUIState } from "@core/ui/UIState";
import { CustomEvents } from "@core/ui";
import { StIcon } from "@core/components/icons";
import { TbArrowUpRight } from "solid-icons/tb";
import { getScreenType } from "@core/lib/ui/getScreenType";
import { stAnalytics } from "@repo/analytics";

export const PromptAutoImprove = () => {
  const { editor } = usePromptContext();
  const [autoImprovePrompt] = useUIState().autoImprovePrompt;
  const [editorMounted, setEditorMounted] = createSignal(false);

  createEffect(() => {
    if (editor() && !editorMounted()) {
      setEditorMounted(true);
    }
  });

  return (
    <Show when={autoImprovePrompt() && editorMounted()}>
      <Gated />
    </Show>
  );
};

const Gated = () => {
  const wire = useWire();
  const { editor, typePrompt, activeCollection, submitPrompt } = usePromptContext();

  const state = useUIState();
  const [, setModalOpen] = state.modal;
  const [, setModalContents] = state.modalContents;

  const [alreadyImproved, setAlreadyImproved] = createSignal(false);

  const client = getRequestClient(wire.services.auth.token);

  const attempts = new Map<Promise<operation.Response<curator.PromptEnhancementsResponse>>, boolean>();
  const [data, setData] = createStore({
    loading: false,
    improvement: null as string | null,
  });

  /**
   * Monitor updates to the editor content. If the user has paused typing for 2 seconds,
   * and there's more than 4 words,send the content to the server for auto-improvement.
   */
  const debouncedImproving = debounce(async (p: { editor: Editor }) => {
    const content = p.editor.getText();
    if (alreadyImproved()) {
      return;
    }

    // Check if there are at least 3 words in the content
    const words = content.trim().split(/\s+/).filter(Boolean);
    if (words.length < 3) {
      return;
    }

    setData({
      loading: true,
    });

    const parsedPrompt = parsePromptTextMentions(content);

    // Using sets to avoid duplicates
    const assets = new Set<string>();
    const collections = new Set<string>();

    for (const mention of parsedPrompt) {
      if (mention.type === "asset") {
        assets.add(mention.id);
      } else if (mention.type === "collection") {
        collections.add(mention.id);
        const descendants = wire.services.collections.getCollectionDescendantsIds(mention.id);
        for (const descendant of descendants) {
          collections.add(descendant);
        }
      }
    }

    if (assets.size === 0 && collections.size === 0) {
      const active = activeCollection();
      if (active) {
        collections.add(active.id);
        const descendants = wire.services.collections.getCollectionDescendantsIds(active.id);
        for (const descendant of descendants) {
          collections.add(descendant);
        }
      }
    }

    const promise = client.controlplane.PromptEnhancer({
      prompt: content,
      type: "auto_improve",
      assets: Array.from(assets),
      collections: Array.from(collections),
    });
    attempts.set(promise, true);

    const response = await promise;

    if (attempts.get(promise)) {
      setData({
        loading: false,
        improvement: response.data?.prompt_enhanced?.content[0] ?? null,
      });
    }
  }, 1500);

  const onUpdate = (p: { editor: Editor }) => {
    const content = p.editor.getText();
    // A change has occurred, so we need to reset all previous auto-improvement attempts.
    attempts.clear();

    setData({
      improvement: null,
      loading: false,
    });

    if (content === "") {
      setAlreadyImproved(false);
      return;
    }

    // Check if there are at least 3 words in the content
    const words = content.trim().split(/\s+/).filter(Boolean);
    if (words.length < 3) {
      return;
    }

    if (alreadyImproved()) {
      return;
    }
    debouncedImproving(p);
  };

  onMount(() => {
    const e = editor();
    if (!e) return;

    onUpdate({ editor: e });
    editor()?.on("update", onUpdate);

    const onSubmitted = () => {
      attempts.clear();
      setData({
        improvement: null,
        loading: false,
      });
    };
    CustomEvents.promptSubmitted.add(onSubmitted);
    onCleanup(() => {
      editor()?.off("update", onUpdate);
      CustomEvents.promptSubmitted.remove(onSubmitted);
    });
  });

  return (
    <div class="text-on-background">
      <p
        class="text-shimmer mb-3 -mt-2 font-medium"
        classList={{ hidden: !data.loading, "inline-block": data.loading }}
      >
        Improving your prompt
      </p>

      <div
        class="w-full min-h-0 flex flex-col px-2 py-1 rounded bg-surface mb-2 type-body-sm"
        classList={{ hidden: data.loading || !data.improvement }}
      >
        <div class="flex items-center gap-2 justify-between">
          <span class="text-xs font-medium">Use suggested prompt:</span>
          <div class="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => {
                const { text } = getPromptTextAndDecorations(editor());

                setModalOpen("improve-prompt-modal");
                setModalContents(() => () => (
                  <ImprovePromptModal
                    prompt={text ?? ""}
                    onClose={() => {
                      setModalOpen("");
                      setModalContents(null);
                    }}
                  />
                ));

                stAnalytics.track("click_tracking", {
                  cta: "auto_improve_select",
                  position: "prompt_bar",
                  screen: getScreenType(),
                  value: "more_options",
                });
              }}
              class="min-w-0 min-h-0 text-on-surface hover:underline bg-surface-decoration rounded leading-tight px-1 text-xs flex-shrink-0 w-auto"
            >
              More options
            </button>
            <button
              type="button"
              onClick={async () => {
                setAlreadyImproved(true);
                await typePrompt(data.improvement || "");
                setAlreadyImproved(true);

                stAnalytics.track("click_tracking", {
                  cta: "auto_improve_select",
                  position: "prompt_bar",
                  screen: getScreenType(),
                  value: "edit",
                });
              }}
              class="min-w-0 min-h-0 text-on-surface hover:underline bg-surface-decoration rounded leading-tight px-1 text-xs flex-shrink-0 w-auto"
            >
              Edit
            </button>
          </div>
        </div>

        <div class="flex gap-4 items-end justify-between">
          <button
            type="button"
            class="flex-auto"
            onClick={async () => {
              setAlreadyImproved(true);
              await typePrompt(data.improvement || "");
              setAlreadyImproved(true);

              stAnalytics.track("click_tracking", {
                cta: "auto_improve_select",
                position: "prompt_bar",
                screen: getScreenType(),
                value: "edit",
              });
            }}
          >
            <p class="leading-tight whitespace-pre-wrap type-body-sm max-h-40 overflow-y-auto">
              <For each={parsePromptTextMentions(data.improvement || "")}>
                {(segment) => (
                  <Switch>
                    <Match when={segment.type === "text" && segment.text}>{(s) => <span>{s()}</span>}</Match>
                    <Match when={segment.type === "collection" && segment}>
                      {(s) => <PromptCollectionCTA id={s().id} name={s().name} />}
                    </Match>
                    <Match when={segment.type === "asset" && segment}>
                      {(s) => <PromptAssetCTA id={s().id} name={s().name} />}
                    </Match>
                  </Switch>
                )}
              </For>
            </p>
          </button>

          <button
            type="button"
            onClick={() => {
              if (data.improvement) {
                submitPrompt({ text: data.improvement, mentionedAssets: [] });

                stAnalytics.track("click_tracking", {
                  cta: "auto_improve_select",
                  position: "prompt_bar",
                  screen: getScreenType(),
                  value: "submit",
                });
              }
            }}
            class="rounded bg-primary text-on-primary flex items-center justify-center min-w-8 min-h-8 w-8 h-8"
          >
            <StIcon icon={TbArrowUpRight} size={16} stroke-width={3} />
          </button>
        </div>
      </div>
    </div>
  );
};
